import React, { Component, ErrorInfo, ReactNode } from 'react';
import { ErrorFallback } from './ErrorFallback';
import { captureBoundaryError } from '@/lib/observability';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /// SCALE-2 — names the boundary in the error report. App.tsx mounts
  /// ~25 of these, one per route; without a name every issue reads
  /// "ErrorBoundary" and you can't tell a crash in /galleries from one
  /// in /billing. Optional so existing call sites keep compiling.
  name?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  /// MKTG-3 — React's component stack for the throw. Captured in
  /// state (not just logged) so `ErrorFallback` can show it in dev.
  /// Without this a crash in a deep tree is undiagnosable from the
  /// screenshot alone, which is exactly how the /orbit nested-Router
  /// crash went a week without a cause.
  componentStack: string | null;
  reloading: boolean;
}

/// Stale-chunk recovery — when Vercel ships a new deploy, the
/// browser's cached index.html still points at the OLD bundle's
/// chunk filenames. Clicking through the app eventually triggers a
/// dynamic import of a chunk that no longer exists, which throws a
/// generic-looking error and presents as random failures mid-flow.
///
/// We detect those errors by their fingerprint (the messages browsers
/// produce when a script tag / module load fails) and force-reload
/// the page so the user gets fresh HTML pointing at the new chunks.
/// We also gate on a sessionStorage flag so a real bug doesn't put
/// the user in a reload loop — only one auto-reload per session.
function isChunkLoadError(error: Error | null): boolean {
  if (!error) return false;
  const msg = `${error.name} ${error.message}`.toLowerCase();
  return (
    msg.includes('chunkloaderror') ||
    msg.includes('loading chunk') ||
    msg.includes('loading css chunk') ||
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes("importing a module script failed") ||
    msg.includes('error loading') && msg.includes('.js') ||
    // Safari + Chrome variant: when the JS chunk request returns the
    // 404 HTML page instead of JS, the browser refuses to execute
    // it as a module and throws this. Same root cause as a
    // ChunkLoadError — the stale-chunk path — so we recover the
    // same way (full reload to get fresh index.html + chunk graph).
    msg.includes('is not a valid javascript mime type') ||
    msg.includes("'text/html' is not a valid")
  );
}

const RELOAD_GUARD_KEY = 'orbit_chunk_reload_attempted';

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    componentStack: null,
    reloading: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, componentStack: null, reloading: false };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ componentStack: errorInfo?.componentStack ?? null });

    // SCALE-2 — report it. A boundary catch is the single highest-value
    // event Orbit produces: the studio is looking at a broken screen
    // RIGHT NOW, and until this line existed the only way anyone found
    // out was the studio saying so.
    //
    // Chunk-load errors are excluded on purpose. They aren't Orbit
    // bugs — they're a cached index.html pointing at a retired bundle
    // after a deploy — and they fire for every user mid-session on
    // every deploy, which would drown the issue stream and burn the
    // error quota on a condition the boundary already auto-recovers
    // from one line below.
    if (!isChunkLoadError(error)) {
      captureBoundaryError(
        error,
        errorInfo?.componentStack,
        this.props.name ?? 'unnamed',
      );
    }

    // Stale-chunk auto-recovery. If the error looks like a failed
    // dynamic import and we haven't already tried once this session,
    // force a hard reload to pull a fresh index.html + chunk graph.
    if (isChunkLoadError(error)) {
      try {
        const alreadyTried = sessionStorage.getItem(RELOAD_GUARD_KEY);
        if (!alreadyTried) {
          sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
          this.setState({ reloading: true });
          // Tiny delay so the browser commits the sessionStorage
          // write before the reload — otherwise on some browsers
          // (Safari especially) the flag is gone and we'd loop.
          setTimeout(() => {
            window.location.reload();
          }, 50);
          return;
        }
        // Already tried this session — surface the real error
        // instead of looping. User can still click "Try again".
        console.warn('Chunk error after a reload attempt — leaving the boundary in place.');
      } catch {
        // sessionStorage blocked (private mode / cookies disabled).
        // Don't gamble on a reload loop — just show the fallback.
      }
    }
  }

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      componentStack: null,
      reloading: false,
    });
  };

  public render() {
    if (this.state.reloading) {
      // Minimal flash while the reload kicks off — users see a
      // momentary blank instead of a scary error screen.
      return null;
    }
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <ErrorFallback
          error={this.state.error}
          componentStack={this.state.componentStack}
          onReset={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}
